/**
 * Password requirement validation utilities
 */

export interface PasswordRequirement {
  id: string;
  label: string;
  validator: (password: string) => boolean;
}

/**
 * Checks if password contains more than 3 consecutive characters from username (email)
 * Uses the same logic as the mobile app for consistency
 * @param username - The username (email)
 * @param password - The password to check
 * @returns true if password contains >3 chars from username, false otherwise
 */
export const containsUsername = (
  username: string | undefined,
  password: string
): boolean => {
  if (!username || !password) return false;

  const usernameParts = username
    .toLowerCase()
    .split(/[@.+]/)
    .filter((part) => part.length >= 4);

  const lowerPassword = password.toLowerCase();

  return usernameParts.some((part) => {
    for (let len = 4; len <= part.length; len++) {
      for (let i = 0; i <= part.length - len; i++) {
        const substring = part.substring(i, i + len);
        if (lowerPassword.includes(substring)) {
          return true;
        }
      }
    }
    return false;
  });
};

/**
 * Checks if password contains the user's last name
 * @param lastName - The user's last name
 * @param password - The password to check
 * @returns true if password contains last name, false otherwise
 */
export const containsLastName = (
  lastName: string | undefined,
  password: string
): boolean => {
  if (!lastName || !password || lastName.length < 2) return false;

  return password.toLowerCase().includes(lastName.toLowerCase());
};

/**
 * Password requirements list following Figma design
 * Returns an array of password requirements with validators based on email and lastName
 * 
 * @param email - The user's email address (optional)
 * @param lastName - The user's last name (optional)
 * @returns Array of password requirements with validators
 * 
 * @note The `noEmail` and `noLastName` requirements are always included.
 * If `email` or `lastName` are not available, those requirements return "not met"
 * to keep requirement visibility and strength scoring consistent.
 * 
 * @important The validators for `noEmail` and `noLastName` create closures that capture
 * the `email` and `lastName` parameters at the time this function is called. If these
 * values change after the requirements array is created, the validators will continue
 * using the old values, leading to incorrect password validation.
 * 
 * To avoid this issue:
 * - Always recreate the requirements array when email or lastName change
 * - When using React hooks, ensure email and lastName are included in dependency arrays
 * - Do not store and reuse the requirements array if email/lastName might change
 * 
 * Example (React):
 * ```tsx
 * const requirements = useMemo(
 *   () => getPasswordRequirements(email, lastName),
 *   [email, lastName] // Important: include email and lastName in dependencies
 * );
 * ```
 */
export const getPasswordRequirements = (
  email?: string,
  lastName?: string
): PasswordRequirement[] => {
  const requirements: PasswordRequirement[] = [
    {
      id: 'length',
      label: 'At least 8 characters (maximum 21 characters)',
      validator: (pwd) => pwd.length >= 8 && pwd.length <= 21,
    },
    {
      id: 'uppercase',
      label: 'At least one uppercase letter',
      validator: (pwd) => /[A-Z]/.test(pwd),
    },
    {
      id: 'lowercase',
      label: 'At least one lowercase letter',
      validator: (pwd) => /[a-z]/.test(pwd),
    },
    {
      id: 'number',
      label: 'At least one number',
      validator: (pwd) => /\d/.test(pwd),
    },
    {
      id: 'special',
      label: 'At least one special character (!@#$%^&*)',
      validator: (pwd) => {
        // Must contain at least one allowed special character
        const hasAllowed = /[!@#$%^&*]/.test(pwd);
        // Must NOT contain any character outside A-Za-z0-9!@#$%^&*
        const onlyAllowed = /^[A-Za-z0-9!@#$%^&*]+$/.test(pwd);
        return hasAllowed && onlyAllowed;
      },
    },
  ];

  // Always include username requirement so it appears in strength/list consistently.
  requirements.push({
    id: 'noEmail',
    label: 'Cannot contain part of username',
    validator: (pwd) => {
      if (!pwd || pwd.length === 0) return false;
      if (!email) return false;
      return !containsUsername(email, pwd);
    },
  });

  // Always include lastName requirement - it must always be validated
  // If lastName is not available, return false (not met) to block form submission
  // This ensures the requirement is always enforced
  requirements.push({
    id: 'noLastName',
    label: 'Cannot contain your last name',
    validator: (pwd) => {
      // Return false (not met) if password is empty
      if (!pwd || pwd.length === 0) return false;
      // If lastName is not available, we cannot validate, so return false (not met)
      // This ensures lastName must be available for password validation to pass
      if (!lastName || lastName.length < 2) return false;
      return !containsLastName(lastName, pwd);
    },
  });

  return requirements;
};

/**
 * Legacy export for backward compatibility
 * @deprecated Use getPasswordRequirements instead
 */
export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = getPasswordRequirements();
